import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import '../../App.css';
import './MangaPage.css';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import MangaApiService from '../../services/MangaApiService';
import MangaCardComponent from '../../components/mangacard/MangaCardComponent';
import { unwrapList } from './mangaStudioUtils';

const STATUSES = [
    { value: 'READING', label: 'Читаю' },
    { value: 'PLAN_TO_READ', label: 'В планах' },
    { value: 'COMPLETED', label: 'Прочитано' },
    { value: 'ON_HOLD', label: 'На паузе' },
    { value: 'DROPPED', label: 'Брошено' },
];

/** Someone's manga lists, laid out like the anime collections page. */
const MangaUserListsPage = () => {
    const { userId } = useParams();
    const [searchParams] = useSearchParams();
    const nickname = searchParams.get('nick');
    const [counts, setCounts] = useState(null);
    const [status, setStatus] = useState(null);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        document.title = nickname ? `Манга ${nickname} на animix` : 'Списки манги на animix';
    }, [nickname]);

    // Open the first non-empty list, so the page never starts on an empty tab.
    useEffect(() => {
        let active = true;
        MangaApiService.getUserMangaProfile(userId)
            .then((profile) => {
                if (!active) {
                    return;
                }
                const lists = profile?.lists || {};
                setCounts(lists);
                const first = STATUSES.find((entry) => Number(lists[entry.value]) > 0);
                setStatus((current) => current || (first ? first.value : STATUSES[0].value));
            })
            .catch(() => active && setStatus((current) => current || STATUSES[0].value));
        return () => {
            active = false;
        };
    }, [userId]);

    useEffect(() => {
        if (!status) {
            return undefined;
        }
        let active = true;
        setLoading(true);
        setFailed(false);
        MangaApiService.getUserMangaListsOf(userId, status)
            .then((response) => {
                if (active) {
                    setItems(unwrapList(response).map((entry) => entry.manga || entry).filter(Boolean));
                }
            })
            .catch(() => {
                if (active) {
                    setItems([]);
                    setFailed(true);
                }
            })
            .finally(() => active && setLoading(false));
        return () => {
            active = false;
        };
    }, [userId, status, reloadKey]);

    const selectedLabel = useMemo(
        () => STATUSES.find((entry) => entry.value === status)?.label || '',
        [status]
    );

    return (
        <PageShell
            sidebar
           
            className="catalog-page collection-page"
        >
            <PageHeader title={nickname ? `Манга ${nickname}` : 'Списки манги'}>
                <div className="ui-chips manga-user-lists-tabs" role="tablist">
                    {STATUSES.map((entry) => {
                        const count = counts ? Number(counts[entry.value]) || 0 : null;
                        return (
                            <button
                                key={entry.value}
                                type="button"
                                role="tab"
                                aria-selected={status === entry.value}
                                className="ui-chip"
                                onClick={() => setStatus(entry.value)}
                            >
                                {entry.label}
                                {count !== null ? <span className="manga-filter-count">{count}</span> : null}
                            </button>
                        );
                    })}
                </div>
            </PageHeader>

            {loading ? <div className="loader" /> : items.length > 0 ? (
                <div className="anime-container">
                    {items.map((manga) => (
                        <MangaCardComponent key={manga.id || manga.slug} manga={manga} isInteractive />
                    ))}
                </div>
            ) : (
                <div className="ui-empty">
                    <h2 className="ui-empty__title">
                        {failed ? 'Не получилось загрузить список' : 'Здесь пока пусто'}
                    </h2>
                    <p className="ui-empty__text">
                        {failed ? 'Попробуйте ещё раз.' : `В списке «${selectedLabel}» пока нет манги.`}
                    </p>
                    {failed ? (
                        <button type="button" className="ui-btn ui-btn--secondary" onClick={() => setReloadKey((key) => key + 1)}>
                            Повторить
                        </button>
                    ) : null}
                </div>
            )}
        </PageShell>
    );
};

export default MangaUserListsPage;
